import React, { useState } from 'react'
import { ConnectedProps, connect } from 'react-redux'
import { compose } from 'redux'
import { Field, InjectedFormProps, change, getFormValues, reduxForm, stopSubmit } from 'redux-form'

import { noop } from 'constants/global'
import { NurserySettingsFormBuilder } from 'services/nurseries/models'

import { getBackendErrors } from 'utils/backendErrors'

import appSelectors from 'services/app/selectors'
import { withFormBuilderSettingsService, withFormBuilderSettingsServiceProps } from 'services/formBuilderSettings'
import formBuilderSettingsSelectors from 'services/formBuilderSettings/selectors'
import { withSnackbarService, withSnackbarServiceProps } from 'services/utils/snackbar'

import { Form, Grid, Radio, Space } from 'components'
import {
  NURSERY_SETTINGS_GROUPS,
} from 'module/Learning/Observations/ObservationFrameworks/ObservationFrameworksContainer'

import i18n from 'translations'

interface TermsAndConditionsFormValues {
  value: string
}

interface TermsAndConditionsProps {
  disabled: boolean
  nurseryFormBuilderSettings: NurserySettingsFormBuilder
}

const TERMS_AND_CONDITIONS_FORM = 'termsAndConditions'

const mapState = (state, { params }) => ({
  formValues: getFormValues(TERMS_AND_CONDITIONS_FORM)(state) as TermsAndConditionsFormValues,
  nurseryOptions: appSelectors.getContextNurseryRouterConfig(state, params),
})

const mapDispatch = {
  changeField: (field, value) => change(TERMS_AND_CONDITIONS_FORM, field, value),
  injectValidation: (data) => stopSubmit(TERMS_AND_CONDITIONS_FORM, data),
}

const connector = connect(mapState, mapDispatch)

type PropsFromRedux = ConnectedProps<typeof connector>

type FinalProps = PropsFromRedux
  & InjectedFormProps<TermsAndConditionsFormValues, TermsAndConditionsProps>
  & TermsAndConditionsProps
  & withSnackbarServiceProps
  & withFormBuilderSettingsServiceProps

enum TermsAndConditionsType {
  TEXT = 'text',
  URL = 'url',
}

const toolbarOptions = {
  // eslint-disable-next-line camelcase
  content_style: 'body { font-family: Figtree, sans-serif; }',
  // eslint-disable-next-line max-len
  toolbar: 'undo redo | bold italic underline strikethrough | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image',
}

const TermsAndConditions: React.FC<FinalProps> = ({
  changeField,
  disabled,
  formBuilderSettingsActions,
  formValues,
  initialValues,
  injectValidation,
  nurseryFormBuilderSettings,
  nurseryOptions,
  snackbarActions,
}) => {
  const initialType = nurseryFormBuilderSettings.isTermsAndConditionsURL
    ? TermsAndConditionsType.URL
    : TermsAndConditionsType.TEXT

  const [previousValue, setPreviousValue] = useState<Record<TermsAndConditionsType, string>>({
    [TermsAndConditionsType.TEXT]: initialType === TermsAndConditionsType.TEXT ? initialValues.value : '',
    [TermsAndConditionsType.URL]: initialType === TermsAndConditionsType.URL ? initialValues.value : '',
  })
  const [type, setType] = useState<TermsAndConditionsType>(initialType)

  const handleFailed = (response) => {
    const errors = getBackendErrors(response)

    if (!errors) {
      return
    }

    injectValidation({
      value: errors.termsAndConditions,
    })
  }

  const handleSubmit = async (isUrl: boolean, content: string) => {
    const payload = formBuilderSettingsSelectors.getPayload(
      nurseryFormBuilderSettings,
      {
        isTermsAndConditionsURL: isUrl,
        termsAndConditions: content,
      },
    )

    if (' ' === payload.termsAndConditions) {
      payload.termsAndConditions = null
    }

    await formBuilderSettingsActions.update({
      body: payload,
      onFailed: handleFailed,
      onSuccess: () => {
        snackbarActions.show({
          message: i18n.t('module:Management:FormBuilder:success'),
        })
      },
      params: [nurseryOptions.id, {
        groups: NURSERY_SETTINGS_GROUPS,
      }],
    })
  }

  const handleBlur = (e) => {
    if (type === TermsAndConditionsType.TEXT) {
      handleSubmit(false, e.target.getContent() || null)
    } else {
      handleSubmit(true, e.target.value || null)
    }
  }

  const handleChangeType = (newType: TermsAndConditionsType) => {
    if (newType === TermsAndConditionsType.TEXT) {
      setPreviousValue({
        [TermsAndConditionsType.URL]: formValues.value,
        [TermsAndConditionsType.TEXT]: '',
      })
    } else {
      setPreviousValue({
        [TermsAndConditionsType.URL]: '',
        [TermsAndConditionsType.TEXT]: formValues.value,
      })
    }

    setType(newType)
    changeField('value', previousValue[newType] || '')
    handleSubmit(newType === TermsAndConditionsType.URL, previousValue[newType] || '')
  }

  return (
    <Form onSubmit={noop}>
      <Grid spacing={20}>
        <Grid.Item>
          <Radio
            checked={type === TermsAndConditionsType.TEXT}
            label={i18n.t('module:Management:FormBuilder:fields:termsAndConditions:text')}
            onChange={() => handleChangeType(TermsAndConditionsType.TEXT)}
          />
        </Grid.Item>
        <Grid.Item>
          <Radio
            checked={type === TermsAndConditionsType.URL}
            label={i18n.t('module:Management:FormBuilder:fields:termsAndConditions:url')}
            onChange={() => handleChangeType(TermsAndConditionsType.URL)}
          />
        </Grid.Item>
      </Grid>
      <Space space="10px" />
      {type === TermsAndConditionsType.TEXT && (
        <Field
          component={Form.TextEditorField}
          disabled={disabled}
          extraOptions={toolbarOptions}
          name="value"
          onBlur={handleBlur}
        />
      )}
      {type === TermsAndConditionsType.URL && (
        <Field
          component={Form.TextField}
          disabled={disabled}
          name="value"
          placeholder={i18n.t('module:Management:FormBuilder:fields:termsAndConditions:yourUrl')}
          onBlur={handleBlur}
        />
      )}
      <Space space="10px" />
    </Form>
  )
}

const enhance = compose(
  withSnackbarService,
  withFormBuilderSettingsService,
  connector,
)

export default (
  reduxForm<
    TermsAndConditionsFormValues,
    TermsAndConditionsProps
  >({ form: TERMS_AND_CONDITIONS_FORM })(enhance(TermsAndConditions))
)
